import { CalendarDays, MapPin, Megaphone, Users } from "lucide-react";
import { Badge, Card, EmptyState, SectionHeading } from "./ui";
import type { EventSetting, PortalPayload } from "../lib/api";
import { formatDateTime, statusLabel } from "../lib/format";

export default function EventOverview({
  event,
  announcements,
  decisionStatus,
}: {
  event: EventSetting;
  announcements: PortalPayload["announcements"];
  decisionStatus: PortalPayload["decisionStatus"];
}) {
  const estimates: Array<{ label: string; value: string; note: string }> = [
    {
      label: "Total cohort",
      value: `${event.totalCohort} students`,
      note: "Second-year seniors + first-year juniors",
    },
    {
      label: "Second-year seniors",
      value: `${event.seniorsCount}`,
      note: "Organizing cohort",
    },
    {
      label: "First-year juniors",
      value: `${event.juniorsCount}`,
      note: "Welcome cohort",
    },
    {
      label: "Expected attendees",
      value: `~${event.expectedAttendees}`,
      note: `Estimate — ${event.participationRateBps / 100}% participation assumed`,
    },
    {
      label: "Event date & time",
      value: event.scheduledAt ? formatDateTime(event.scheduledAt) : "To be decided",
      note: event.scheduledAt ? "Provisional until finalized" : "Vote in the date poll to decide",
    },
    {
      label: "Venue",
      value: event.venue ?? "To be decided",
      note: event.venue ? "Provisional until finalized" : "Suggested venues appear in voting",
    },
    {
      label: "Event status",
      value: statusLabel(event.status),
      note: `${decisionStatus.openPolls} polls open · ${decisionStatus.decidedPolls} decided`,
    },
    {
      label: "Decisions pending",
      value: `${Math.max(decisionStatus.totalPolls - decisionStatus.decidedPolls, 0)}`,
      note: "Date, menu, contribution, transport and more",
    },
  ];

  return (
    <section id="overview" className="mx-auto max-w-shell scroll-mt-24 px-5 py-24 md:px-10">
      <SectionHeading
        eyebrow="Event overview"
        title="A warm welcome for the BBA batch of 2026"
        description={event.description || undefined}
      />

      <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {estimates.map((item) => (
          <Card key={item.label} className="p-6">
            <p className="text-xs font-medium uppercase tracking-wider text-wandor-muted">
              {item.label}
            </p>
            <p className="mt-2 text-xl font-semibold text-white">{item.value}</p>
            <p className="mt-1.5 text-sm text-wandor-muted">{item.note}</p>
          </Card>
        ))}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Badge tone="warning">Planning estimates — not confirmed figures</Badge>
        <Badge tone="neutral">
          <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" /> Date:{" "}
          {event.scheduledAt ? formatDateTime(event.scheduledAt) : "To be decided"}
        </Badge>
        <Badge tone="neutral">
          <MapPin className="h-3.5 w-3.5" aria-hidden="true" /> Venue: {event.venue ?? "To be decided"}
        </Badge>
        <Badge tone="neutral">
          <Users className="h-3.5 w-3.5" aria-hidden="true" /> ~{event.expectedAttendees} expected
        </Badge>
      </div>

      {/* Venue & lunch details (Phase: confirmed vs awaiting) */}
      <div className="mt-10">
        <Card className="p-6 md:p-8">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-xl font-semibold text-white">Venue &amp; lunch details</h3>
              <p className="mt-1 max-w-2xl text-sm text-white/50">
                The public page distinguishes confirmed information from details still under
                consideration. Nothing here is guessed.
              </p>
            </div>
            <Badge tone={event.venueStatus === "confirmed" ? "success" : "warning"}>
              {event.venueStatus === "confirmed"
                ? "Venue confirmed"
                : event.venueStatus === "under_consideration"
                  ? "Venue under consideration"
                  : "Venue awaiting confirmation"}
            </Badge>
          </div>

          <dl className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/50">Venue / hall</dt>
              <dd className="mt-1 text-lg font-medium text-white">
                {event.venueName ?? "Venue awaiting confirmation"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/50">Floor</dt>
              <dd className="mt-1 text-lg font-medium text-white">
                {event.venueFloor ?? "Floor details awaiting confirmation"}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/50">Address</dt>
              <dd className="mt-1 text-lg font-medium text-white">
                {event.venueAddress ?? "Address awaiting confirmation"}
                {event.venueMapUrl && (
                  <a
                    href={event.venueMapUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 inline-flex items-center gap-1 rounded-full border border-white/15 px-2.5 py-1 text-xs font-medium text-wandor-prompt transition-colors hover:border-wandor-prompt/50 hover:text-white"
                  >
                    <MapPin className="h-3 w-3" aria-hidden="true" />
                    Directions
                  </a>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/50">Lunch timing</dt>
              <dd className="mt-1 text-lg font-medium text-white">
                {event.timingNote || "Lunch"}
                {event.lunchTime ? (
                  <span className="text-white/50"> · {event.lunchTime}</span>
                ) : (
                  <span className="block text-sm font-normal text-white/50">
                    Serving time awaiting confirmation
                  </span>
                )}
              </dd>
            </div>
          </dl>

          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/50">
                Lunch date &amp; event start
              </dt>
              <dd className="mt-1 text-white">
                {event.scheduledAt ? (
                  <span className="text-lg font-medium">{formatDateTime(event.scheduledAt)}</span>
                ) : (
                  <span className="text-lg font-medium">To be decided</span>
                )}
                <span className="ml-2 text-xs text-white/50">
                  — the date poll opens when the committee publishes candidate dates
                </span>
              </dd>
            </div>
            <div>
              <dt className="text-xs font-medium uppercase tracking-wider text-white/50">
                Booking details (duration, capacity, advance)
              </dt>
              <dd className="mt-1 text-white">
                {event.venueBookingNote ? (
                  <span className="text-sm">{event.venueBookingNote}</span>
                ) : (
                  <span className="text-sm text-white/50">
                    Booking charge, seating capacity, duration and advance terms await the vendor
                    quotation — nothing has been booked on this site.
                  </span>
                )}
              </dd>
            </div>
          </div>
        </Card>
      </div>

      <div className="mt-14">
        <div className="mb-6 flex items-center gap-3">
          <Megaphone className="h-5 w-5 text-wandor-prompt" aria-hidden="true" />
          <h3 className="text-2xl font-semibold text-white">Announcements &amp; deadlines</h3>
        </div>
        {announcements.length === 0 ? (
          <EmptyState
            title="No announcements yet"
            body="The organizing committee will post event updates and deadlines here."
          />
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            {announcements.map((a) => (
              <Card key={a.id} className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <h4 className="text-lg font-semibold text-white">{a.title}</h4>
                  {a.pinned && <Badge tone="accent">Pinned</Badge>}
                </div>
                <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-wandor-muted">
                  {a.body}
                </p>
                <p className="mt-3 text-xs text-white/40">
                  Posted {formatDateTime(a.createdAt)}
                </p>
              </Card>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
